import type { FC } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { cssVariable } from 'app/utils/style';
import { round } from 'app/utils/number';
import { sort } from 'app/utils/array';
import Tooltip from 'app/components/Tooltip';
import { localizedHeroName } from 'app/i18n/utils';
import { getCDNUrl } from 'app/utils/api_url';

import style from './style.module.scss';

interface Props {
  heroes: Hero[] | HeroStat[];
  orientation?: Orientation;
  hidePercentage?: boolean;
  containerClassName?: string;
  heroClassName?: string;
  iconClassName?: string;
  shiftImages?: boolean;
  type?: 'pick' | 'ban';
  videogame: 'dota2' | 'lol';
  placeholderSize?: number;
  showName?: boolean;
}

const HeroesIcon: FC<Props> = ({
  heroes,
  orientation = 'normal',
  hidePercentage = false,
  containerClassName,
  heroClassName,
  iconClassName,
  shiftImages = false,
  type = 'pick',
  videogame,
  placeholderSize = 0,
  showName = false
}) => {
  const shiftedProperty = orientation === 'reversed' ? 'right' : 'left';

  const { t } = useTranslation();

  const sortedHeroes = heroes.length
    ? sort(heroes, (hero: Hero | HeroStat) =>
        'presence_percentage' in hero ? hero.presence_percentage : true
      )
    : Array(placeholderSize).fill(null);

  return (
    <div
      className={classNames(
        style.heroesContainer,
        { [style.reversed]: orientation === 'reversed' },
        containerClassName
      )}
    >
      {sortedHeroes.map((hero, index) => (
        <div
          key={hero?.id || index}
          className={classNames(style.hero, heroClassName)}
          style={
            shiftImages ? { [shiftedProperty]: `${(sortedHeroes.length - (index + 1)) * 15}%` } : {}
          }
        >
          <div className={style.heroNameContainer}>
            <Tooltip content={localizedHeroName(hero, videogame, t)} active={!!hero && !showName}>
              {hero && (
                <span
                  style={cssVariable('hero', `url(${getCDNUrl(hero.image_url)})`)}
                  className={classNames(
                    style.heroImage,
                    { [style.reversed]: orientation === 'reversed' },
                    iconClassName
                  )}
                />
              )}
              {!hero && (
                <span
                  className={classNames(
                    style.heroImage,
                    { [style.reversed]: orientation === 'reversed' },
                    style.heroPlaceholder,
                    iconClassName
                  )}
                >
                  {' '}
                  /{' '}
                </span>
              )}
            </Tooltip>
            {showName && (
              <span className={style.heroName}>{localizedHeroName(hero, videogame, t)}</span>
            )}
          </div>
          {!hidePercentage && (
            <div>
              {hero?.presence_percentage ? (
                <div className={style.percentage}>{round(hero.presence_percentage * 100, 0)}%</div>
              ) : (
                <div className={style.percentage}>-</div>
              )}
              {hero?.number_of_picks ? (
                <div className={style.numberOfPicks}>
                  {t(`component_heroes_${type}`, { numberOfPicks: hero.number_of_picks })}
                </div>
              ) : (
                <div className={style.numberOfPicks}>-</div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

HeroesIcon.displayName = 'HeroesIcon';

export default HeroesIcon;
